import { useQueryTeamDetails } from '@/hooks/query/useQueryTeamDetails';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useIntercom } from '@/hooks/useIntercom';
import { getInvitationDataFromURL } from '@/utils/getInvitationDataFromURL/getInvitationDataFromURL';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyP } from '@heidi/design-system/typography';
import { getJoinFailVariant, joinTeamFailMessages } from '@heidi/workspace';
import { atom, useAtom } from 'jotai';
import { AlertTriangle } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const JoinTeamErrorTypeAtom = atom<string | null>(null);

interface Props {
  /** Overrides the URL-derived team id for in-app (non-/join-team) callers. */
  teamId?: string;
  /** In-app callers pass this to reset their local flow; defaults to the legacy /scribe navigation. */
  onReturn?: () => void;
}

function TeamMemberJoinFailModal({ teamId: teamIdProp, onReturn }: Props = {}) {
  const { showMessageModal } = useIntercom();
  const { data: userData } = useQueryUser();
  const [joinTeamErrorType] = useAtom(JoinTeamErrorTypeAtom);
  const router = useRouter();
  const intl = useIntl();
  const teamId = teamIdProp ?? getInvitationDataFromURL().teamId;
  const { data: teamDetails } = useQueryTeamDetails(teamId);

  const variant = useMemo(
    () =>
      getJoinFailVariant(joinTeamErrorType, userData?.country, teamDetails?.team.country),
    [joinTeamErrorType, teamDetails?.team.country, userData?.country]
  );

  const teamName =
    teamDetails?.team.name ?? intl.formatMessage(joinTeamFailMessages.fallbackTeamName);

  const supportLink = useCallback(
    (chunks: ReactNode) => (
      <Link href="" onClick={() => showMessageModal()} className="text-text-link">
        {chunks}
      </Link>
    ),
    [showMessageModal]
  );

  const title = useMemo(() => {
    switch (variant) {
      case 'alreadyJoined':
      case 'regionMismatch':
      case 'generic':
        return (
          <FormattedMessage
            {...joinTeamFailMessages.couldNotAddTitle}
            values={{ teamName }}
          />
        );
      case 'notFound':
        return <FormattedMessage {...joinTeamFailMessages.notFoundTitle} />;
      case 'notAllowedToAdd':
        return (
          <FormattedMessage
            {...joinTeamFailMessages.notAllowedToAddTitle}
            values={{ teamName }}
          />
        );
      case 'userNotAllowed':
        return <FormattedMessage {...joinTeamFailMessages.userNotAllowedTitle} />;
      case 'invitationNotFound':
        return <FormattedMessage {...joinTeamFailMessages.invitationNotFoundTitle} />;
      case 'invitationExpired':
        return <FormattedMessage {...joinTeamFailMessages.invitationExpiredTitle} />;
      default:
        return null;
    }
  }, [teamName, variant]);

  const description = useMemo(() => {
    switch (variant) {
      case 'alreadyJoined':
        return (
          <DialogDrawerDescription>
            {isHeidiSupportReachable ? (
              <FormattedMessage
                {...joinTeamFailMessages.alreadyJoinedDescription}
                values={{ support: supportLink }}
              />
            ) : (
              <FormattedMessage {...joinTeamFailMessages.alreadyJoinedDescriptionViaIt} />
            )}
          </DialogDrawerDescription>
        );
      case 'notFound':
        return (
          <DialogDrawerDescription>
            <FormattedMessage {...joinTeamFailMessages.notFoundDescription} />
          </DialogDrawerDescription>
        );
      case 'notAllowedToAdd':
        return (
          <DialogDrawerDescription>
            <FormattedMessage {...joinTeamFailMessages.notAllowedToAddDescription} />
          </DialogDrawerDescription>
        );
      case 'userNotAllowed':
        return (
          <DialogDrawerDescription>
            <FormattedMessage {...joinTeamFailMessages.userNotAllowedDescription} />
          </DialogDrawerDescription>
        );
      case 'invitationNotFound':
        return (
          <DialogDrawerDescription>
            <FormattedMessage {...joinTeamFailMessages.invitationNotFoundDescription} />
          </DialogDrawerDescription>
        );
      case 'invitationExpired':
        return (
          <DialogDrawerDescription>
            <FormattedMessage {...joinTeamFailMessages.invitationExpiredDescription} />
          </DialogDrawerDescription>
        );
      case 'regionMismatch':
        return (
          <DialogDrawerDescription>
            {isHeidiSupportReachable ? (
              <FormattedMessage
                {...joinTeamFailMessages.regionMismatchDescription}
                values={{ contact: supportLink }}
              />
            ) : (
              <FormattedMessage
                {...joinTeamFailMessages.regionMismatchDescriptionViaIt}
              />
            )}
          </DialogDrawerDescription>
        );
      case 'generic':
        return (
          <DialogDrawerDescription>
            {isHeidiSupportReachable ? (
              <FormattedMessage
                {...joinTeamFailMessages.genericDescription}
                values={{ support: supportLink }}
              />
            ) : (
              <FormattedMessage {...joinTeamFailMessages.genericDescriptionViaIt} />
            )}
          </DialogDrawerDescription>
        );
      default:
        return null;
    }
  }, [supportLink, variant]);

  return (
    <DialogDrawer open={true}>
      <DialogDrawerContent
        className="mx-auto flex flex-col items-start justify-start gap-4 p-[32px] align-middle backdrop-blur-[3px] sm:w-[500px]"
        hideCloseIcon
      >
        <div>
          <FramedIcon state="error" size="lg" icon={<AlertTriangle />} />
          <div className="flex w-full flex-col gap-2">
            <TypographyP className="w-full p-0 font-semibold">{title}</TypographyP>
            {description}
          </div>
        </div>

        <div className="mt-4 flex w-full gap-2">
          <Button
            size="lg"
            disableAutomaticSvgSizing
            className="flex-1"
            onClick={() => {
              if (onReturn) {
                onReturn();
              } else {
                void router.push('/scribe');
              }
            }}
          >
            <FormattedMessage {...joinTeamFailMessages.returnButton} />
          </Button>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}

export default TeamMemberJoinFailModal;
